import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import styles from "./LandingPage.module.css";
import {
  FaArrowRight,
} from "react-icons/fa";
import TestimonialSeven from "../Testimonials/Testimonial-Seven";
import RoleSelectionModal from "../Modal/loginModal";
import PricingSection from "./PricingSection";
import UserRoles from "./UserRoles/UserRoles";
import { useAppContext } from "@/context/Context";
import { useUser } from "@/context/UserContext";
import SimpleImageSlider from "./SimpleImageSlider";


const LandingPage = () => {


  const { isLightTheme } = useAppContext();
  const { user } = useUser();

  const [stats, setStats] = useState({
    totalInstructor: 0,
    totalStudents: 0,
    totalHRs: 0,
  });

  // Add state for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("register");

  const slideImages = [
    '/images/slideshow/emdeon-slide5.png',
    '/images/slideshow/emdeon-slide4.jpeg',
    '/images/slideshow/emdeon-slide17.png',
    '/images/slideshow/emdeon-slide13.jpeg',
    '/images/slideshow/emdeon-slide9.png',
    '/images/slideshow/emdeon-slide12.jpeg',
  ];


  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/getdashboarddetails`
        );
        const data = await res.json();
        if (data && data.data) {
          setStats(data.data);
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };

    fetchStats();
  }, []);

  // Handler to open modal
  const openModal = (type = "register") => {
    setModalType(type);
    setIsModalOpen(true);
  };

  return (
    <div className={`${styles.landingContainer} ${!isLightTheme ? styles.darkMode : ""}`}>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <video autoPlay muted loop playsInline className={styles.heroVideo}>
            <source src="/MainHome/course.mp4" type="video/mp4" />
          </video>
          <div className={styles.heroOverlay}></div>
        </div>

        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            The Future of <br />
            <span className={styles.cliptext}>Learning & Hiring</span> is Here.
          </h1>
          <p className={styles.heroSubtitle}>
            A unified platform for learning, teaching, and hiring
          </p>
          <p className={styles.heroDescription}>
            ELMS+ enables students to develop practical skills, instructors to scale their expertise, and organizations to recruit verified talent with confidence
          </p>

          <div className={styles.ctaGroup}>
            <Link
              href="/home"
              className="rbt-btn btn-gradient hover-icon-reverse"
            >
              <span className="icon-reverse-wrapper">
                <span className="btn-text">Get Started</span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
              </span>
            </Link>
            <Link
              href="/learn-more"
              className={`rbt-btn ${styles.btnSecondary}`}
            >
              <span className="icon-reverse-wrapper">
                <span className="btn-text">Learn More</span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className={styles.aboutSection}>
        <div className={styles.aboutContainer}>
          <div className={styles.aboutContent}>
            <span className={styles.sectionLabel}>About The Platform</span>
            <h2 className={styles.sectionHeading}>
              More Than Just an LMS. <br />A Complete Ecosystem.
            </h2>
            <p className={styles.aboutText}>
              Traditional education platforms focus only on content delivery.
              Recruitment platforms focus only on resumes.{" "}
              <strong>Transparent Education</strong> combines both.
            </p>
            <p className={styles.aboutText}>
              Our mission is to create a transparent pathway from education to
              employment. Students don&apos;t just take courses; they build a
              verified portfolio of skills. Instructors don&apos;t just teach;
              they shape the future workforce. Companies don&apos;t just guess;
              they hire based on data-backed performance.
            </p>
            <div style={{ marginTop: "30px" }}>
              {/* Updated "Join the Revolution" button to open modal */}
              <button
                onClick={() => openModal("register")}
                className="rbt-btn btn-gradient hover-icon-reverse"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                <span className="icon-reverse-wrapper">
                  <span className="btn-text">Join the Revolution</span>
                  <span className="btn-icon">
                    <i className="feather-arrow-right"></i>
                  </span>
                  <span className="btn-icon">
                    <i className="feather-arrow-right"></i>
                  </span>
                </span>
              </button>
            </div>
          </div>
          <div className={styles.aboutImage}>
            <SimpleImageSlider images={slideImages} isLightTheme={isLightTheme} />
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <UserRoles isLightTheme={isLightTheme} />

      {/* Pricing Section */}
      {(user?.role === "hr" || user?.role === "admin") && <PricingSection />}

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.statsGrid}>
          <div className={styles.statItem}>
            <span className={styles.statNumber}>
              {stats.totalStudents || "1000+"}
            </span>
            <span className={styles.statLabel}>Active Learners</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statNumber}>
              {stats.totalInstructor || "50+"}
            </span>
            <span className={styles.statLabel}>Expert Instructors</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statNumber}>{stats.totalHRs || "20+"}</span>
            <span className={styles.statLabel}>Corporate Partners</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statNumber}>100%</span>
            <span className={styles.statLabel}>Career Growth</span>
          </div>
        </div>
      </section>

      {/* Video Explainer Section */}
      <section className={styles.videoSection}>
        <div className={styles.videoContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>See It In Action</span>
            <h2 className={styles.sectionHeading}>
              Experience the Future of Education & Hiring
            </h2>
            <p
              className={styles.sectionText}
              style={{ maxWidth: "700px", margin: "0 auto" }}
            >
              Watch how Transparent Education transforms the experience for
              everyone involved.
            </p>
          </div>

          <div className={styles.videoGrid}>
            <div className={styles.videoCard}>
              <div className={styles.videoWrapper}>
                <video
                  controls
                  className={styles.videoPlayer}
                  poster="/assets/images/video-poster-1.jpg"
                >
                  <source src="/MainHome/course.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className={styles.videoContent}>
                <h3>For Learners</h3>
                <p>Discover how to build your verified skill portfolio.</p>
              </div>
            </div>

            <div className={styles.videoCard}>
              <div className={styles.videoWrapper}>
                <video controls className={styles.videoPlayer}>
                  <source src="/MainHome/course-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className={styles.videoContent}>
                <h3>For Instructors</h3>
                <p>See the tools that help you monetize your expertise.</p>
              </div>
            </div>

            <div className={styles.videoCard}>
              <div className={styles.videoWrapper}>
                <video controls className={styles.videoPlayer}>
                  <source src="/MainHome/featured.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className={styles.videoContent}>
                <h3>For Companies</h3>
                <p>
                  Learn how to hire based on verified data, not just resumes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <div className="rbt-testimonial-area bg-color-white rbt-section-gap overflow-hidden">
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>Success Stories</span>
          <h2 className={styles.sectionHeading}>Trusted by the Best</h2>
        </div>
        <TestimonialSeven />
      </div>

      {/* CTA Banner */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaContent}>
          <h2>Ready to Transform Your Future?</h2>
          <p>
            Join the thousands of learners, instructors, and companies already
            using Transparent Education to achieve their goals.
          </p>
          {/* Updated "Get Started Today" button to open modal */}
          <button
            onClick={() => openModal("register")}
            className={styles.btnWhite}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            Get Started Today
          </button>
        </div>
      </section>

      {/* Role Selection Modal */}
      <RoleSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={modalType}
      />
    </div>
  );
};

export default LandingPage;